import React, { useState, useEffect } from 'react';
import { Wifi, Search, Bell, User, Menu, TrendingUp, TrendingDown } from 'lucide-react';
import SearchModal from '../SearchModal';
import { cbuApi } from '../../services/api';
import type { ExchangeRateData } from '../../services/api/cbuApi';

interface Props {
  onNavigate: (view: string) => void;
  onMenuToggle?: () => void;
  isMobile?: boolean;
  openSearchTrigger?: number;
}

// Key currencies for the ticker
const TICKER_CURRENCIES = ['USD', 'EUR', 'RUB'];

export const TopBar: React.FC<Props> = ({ onNavigate, onMenuToggle, isMobile = false, openSearchTrigger = 0 }) => {
  const [time, setTime] = useState(new Date());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [fxRates, setFxRates] = useState<ExchangeRateData[]>([]);
  const [fxSource, setFxSource] = useState<'live' | 'cached' | 'static'>('static');

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch CBU exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await cbuApi.fetchExchangeRates();
        if (response.data) {
          const tickerRates = response.data.rates.filter(r => TICKER_CURRENCIES.includes(r.currency));
          setFxRates(tickerRates);
          setFxSource(response.data.source);
        }
      } catch (err) {
        console.warn('Failed to fetch FX rates for ticker:', err);
      }
    };
    fetchRates();
    // Refresh every 15 minutes
    const interval = setInterval(fetchRates, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Open search when trigger changes (from keyboard shortcuts)
  useEffect(() => {
    if (openSearchTrigger > 0) {
      setIsSearchOpen(true);
    }
  }, [openSearchTrigger]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="h-14 md:h-9 lg:h-8 bg-terminal-header border-b border-terminal-border flex items-center px-1 md:px-3 gap-1 md:gap-4">
      {/* Mobile Menu Button */}
      <button
        onClick={onMenuToggle}
        className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-white active:bg-terminal-bg/50 transition-colors rounded"
        aria-label="Open menu"
      >
        <Menu size={22} />
      </button>

      {/* Left Section */}
      <div className="flex items-center gap-2 md:gap-3">
        <span className="text-data-amber font-bold text-xs">
          <span className="md:hidden">SR 2.0</span>
          <span className="hidden md:inline">SILK ROAD 2.0</span>
        </span>
        <span className="hidden lg:inline text-gray-500 text-[10px]">| UZBEKISTAN MARKET TERMINAL</span>
        <div className="hidden sm:flex items-center gap-1 ml-1 md:ml-2">
          <Wifi size={10} className={fxSource === 'live' ? 'text-data-green' : 'text-data-amber'} />
          <span className={`text-[8px] md:text-[9px] ${fxSource === 'live' ? 'text-data-green' : 'text-data-amber'}`}>
            {fxSource === 'live' ? 'LIVE' : fxSource === 'cached' ? 'CACHED' : 'STATIC'}
          </span>
        </div>

        {/* FX Ticker - CBU Exchange Rates */}
        {fxRates.length > 0 && (
          <div className="hidden lg:flex items-center gap-3 ml-2 pl-2 border-l border-terminal-border">
            {fxRates.map(rate => (
              <div key={rate.currency} className="flex items-center gap-1">
                <span className="text-[9px] text-gray-500">{rate.currency}/UZS</span>
                <span className="text-[9px] font-mono text-white">{rate.rate.toFixed(2)}</span>
                {rate.change !== 0 && (
                  <span className={`flex items-center text-[8px] font-mono ${
                    rate.change > 0 ? 'text-data-green' : 'text-data-red'
                  }`}>
                    {rate.change > 0 ? (
                      <TrendingUp size={8} />
                    ) : (
                      <TrendingDown size={8} />
                    )}
                    {rate.change > 0 ? '+' : ''}{rate.change.toFixed(2)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Center Section - Search (hidden on mobile, shows icon button instead) */}
      <div className="flex-1 flex justify-center">
        {/* Mobile Search Icon */}
        <button
          onClick={() => setIsSearchOpen(true)}
          className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-white active:bg-terminal-bg/50 transition-colors rounded"
          aria-label="Search"
        >
          <Search size={20} />
        </button>

        {/* Desktop/Tablet Search Bar */}
        <div className="hidden md:block w-full max-w-xs lg:max-w-md">
          <button
            onClick={() => setIsSearchOpen(true)}
            className="w-full relative bg-terminal-bg border border-terminal-border rounded px-7 py-1 text-left hover:border-data-amber/50 transition-colors"
          >
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
            <span className="text-[9px] lg:text-[10px] text-gray-600">
              <span className="hidden lg:inline">Search indicators, sectors...</span>
              <span className="lg:hidden">Search...</span>
            </span>
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 text-[9px]">⌘K</span>
          </button>
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2 md:gap-4">
        <div className="flex items-center gap-1 text-[9px] md:text-[10px]">
          <span className="text-gray-300">{formatTime(time)}</span>
          <span className="hidden sm:inline text-gray-500">UTC+5</span>
        </div>

        <button className="hidden sm:flex md:hidden min-w-[44px] min-h-[44px] items-center justify-center relative text-gray-500 hover:text-gray-300 active:bg-terminal-bg/50 transition-colors rounded">
          <Bell size={18} />
          <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-data-red rounded-full" />
        </button>

        <button className="hidden md:flex relative text-gray-500 hover:text-gray-300 transition-colors p-1">
          <Bell size={14} />
          <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-data-red rounded-full" />
        </button>

        <button className="hidden md:flex items-center gap-1 text-gray-500 hover:text-gray-300 transition-colors">
          <User size={14} />
          <span className="text-[10px]">NATEX</span>
        </button>
      </div>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={(view) => {
          onNavigate(view);
          setIsSearchOpen(false);
        }}
      />
    </div>
  );
};

export default TopBar;
